import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logout } from '../store/slices/authSlice';
import { LogIn, X } from 'lucide-react';

/**
 * Global modal shown when the user's session token has expired.
 * Listens for the custom "session-expired" DOM event dispatched by the HTTP client.
 */
export default function SessionExpiredModal() {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleSessionExpired = useCallback(() => {
    setVisible(true);
  }, []);

  useEffect(() => {
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [handleSessionExpired]);

  if (!visible) return null;

  const handleDismiss = () => {
    setVisible(false);
  };

  const handleLogin = () => {
    // Clean Redux auth state
    dispatch(logout());
    setVisible(false);
    // Navigate to login, remembering where the user was
    navigate('/login', { state: { from: location }, replace: true });
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="modal-panel relative rounded-2xl max-w-sm w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('common.close')}
          className="modal-close-btn absolute top-3 right-3 z-10 p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5 text-center">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <LogIn className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-lg font-bold text-white">
            {t('sessionExpired.title')}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-textSecondary text-center text-sm leading-relaxed">
            {t('sessionExpired.message')}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 space-y-3">
          <button
            onClick={handleLogin}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors"
          >
            <LogIn className="w-4 h-4" />
            {t('sessionExpired.login')}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="btn-secondary w-full"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
